import Paystack from "@paystack/inline-js";
import { CountryCode } from "libphonenumber-js";
import allCountries from "world-countries/countries.json";
import { TagType } from "../components/cards/tagCard";
import { CooperativeAction } from "../redux/features/approvals/approvals.type";
import { DashTransactionType } from "../redux/features/auth/auth.type";
import { CooperativeMembershipFormFieldType } from "../redux/features/cooperative/cooperative.type";
import { CooperativeTransactionWalletType } from "../redux/features/finances/finances.type";
import {
  LoanDisbursementMethod,
  LoanRepaymentMethod,
  LoanRepaymentType,
  LoanStatus,
} from "../redux/features/loans/loans.type";
import {
  ApprovalChainMapValue,
  CooperativePageTab,
  ManualPaymentMethod,
  TransactionCategory,
} from "./types";

export const cooperativeSizes = [
  {
    text: "1-10",
    value: [1, 10],
  },
  {
    text: "11-30",
    value: [11, 30],
  },
  {
    text: "31-99",
    value: [31, 99],
  },
  {
    text: "100-499",
    value: [100, 499],
  },
  {
    text: "500+",
    value: [500],
  },
];

export const OtpMaxTime = 90; // Seconds

export const FE_STAGING_URL = "www.staging.thriftcorp.co";
export const FE_PROD_URL = "www.thriftcorp.co";
export const FE_APP_STAGING_URL = "www.app-staging.thriftcorp.co";
export const FE_APP_PROD_URL = "www.app.thriftcorp.co";

export const INACTIVITY_TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT
  ? Number(process.env.REACT_APP_INACTIVITY_TIMEOUT)
  : 1000 * 60 * 500; // 5 minutes

export const FAILED_LOAN_STATUS = "failedDisbursement";

export const permissionList = {
  CAN_ADD_PAYMENT: "can.add.payment",
  CAN_ADD_MEMBER: "can.add.member",
  CAN_APPROVE_MEMBER: "can.approve.member",
  CAN_APPROVE_PAYMENT: "can.approve.payment",
  CAN_APPROVE_LOANS: "can.approve.loans",
};

export const CLIENT_COOKIE_KEY = "_tC_token";

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const API_REQUEST_TIMEOUT = 600000;

export const OTPMAXTIME = 90; //seconds

export const SKELETON_BASE_COLOR = "#e0e0e0";
export const SKELETON_HIGHLIGHT_COLOR = "#ffffff80";

export const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const CLOUDINARY_URL = process.env.REACT_APP_CLOUDINARY_URL;
export const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET ?? "";
export const CLOUDINARY_UPLOAD_FOLDER =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_FOLDER ?? "";

export const MEMBERSHIP_FORM_FIELD_ICONS_MAP: Record<
  CooperativeMembershipFormFieldType,
  string
> = {
  checkbox: "checkbox-active",
  date: "calendar",
  datetime: "calendar",
  email: "sms",
  file: "file-upload-icon",
  File: "file-upload-icon",
  radio: "checkbox-active",
  select: "checkbox-active",
  text: "text-left-align",
  textarea: "text-left-align",
};

export const MEMBERSHIP_FORM_FIELD_TITLE_MAP: Record<
  CooperativeMembershipFormFieldType,
  string
> = {
  checkbox: "Multi-choice",
  date: "Date",
  datetime: "Date and Time",
  email: "Email",
  file: "File",
  File: "File",
  radio: "Single Choice",
  select: "Multi-choice",
  text: "Short answer",
  textarea: "Long answer",
};

export const approvalChainsMap: Record<
  CooperativeAction,
  ApprovalChainMapValue
> = {
  changeOfficialApproval: {
    title: "Official Change Approval",
    subtext: "Configure how loans will be approved within the cooperative.",
    initiatorPermission: [],
    approverPermissions: [],
  },
  loanApproval: {
    title: "Loans Approval ",
    subtext: "Configure how loans will be approved within the cooperative.",
    initiatorPermission: ["can.approve.loans"],
    approverPermissions: ["can.approve.loans"],
  },
  memberApproval: {
    title: "Membership Approval",
    subtext:
      "Configure how new members are approved before joining the cooperative.",
    initiatorPermission: ["can.add.member", "can.approve.member"],
    approverPermissions: ["can.approve.member"],
  },
  memberRemoval: {
    title: "Membership Exit",
    subtext: "Configure how member exits are approved within the cooperative.",
    initiatorPermission: ["can.remove.member", "can.approve.removal"],
    approverPermissions: ["can.approve.removal"],
  },
  paymentApproval: {
    title: "Payments Approval",
    subtext:
      "Configure how manually entered/uploaded payments (e.g contributions, loan repayments etc.) will be approved within the cooperative.",
    initiatorPermission: ["can.add.payment", "can.approve.payment"],
    approverPermissions: ["can.approve.payment"],
  },
  settingApproval: {
    title: "Settings Approval",
    subtext:
      "Configure how configuration changes will be approved within the cooperative.",
    initiatorPermission: [],
    approverPermissions: [],
  },
};

export const payStackPopup = new Paystack();

export const transactioncategoryMap: Record<
  TransactionCategory,
  {
    title: string;
  }
> = {
  contribution: {
    title: "Contribution",
  },
  deposit: {
    title: "Deposit",
  },
  shares: {
    title: "Shares",
  },
  cooperative_wallet: { title: "Cooperative fund inflow" },
  loanDisbursement: { title: "Loan disbursement" },
  loanrepayment: { title: "Loan repayment" },
  registrationfee: { title: "Registration fee" },
  saving: { title: "Contribution" },
  withdrawal: { title: "Cooperative fund outflow" },
};

export const ManualPaymentMethods: ManualPaymentMethod[] = [
  "bank",
  "cash",
  "transfer",
  "cheque",
];

export const ManualPaymentMethodsMap: Record<ManualPaymentMethod, string> = {
  bank: "Bank deposit",
  card: "Card",
  cash: "Cash",
  cheque: "Cheque",
  crypto: "Crypto",
  mobile_money: "Mobile money",
  online: "Online",
  pos: "POS",
  transfer: "Bank transfer",
  ussd: "USSD",
  wallet: "Wallet",
};

export const DashboardTransactionTypeMap: Record<
  DashTransactionType,
  {
    text: string;
    cooperativeTab: CooperativePageTab;
  }
> = {
  deposit: { text: "Deposit", cooperativeTab: "deposits" },
  saving: { text: "Contribution", cooperativeTab: "contributions" },
  shares: { text: "Share", cooperativeTab: "shares" },
  loan_repay: { text: "Loan Repayment", cooperativeTab: "loans" },
};

export const domain = window.location.hostname;

export const APP_SCHEME = window.location.protocol;

export const APP_PORT = window.location.port;

export const isLocalhost =
  domain.includes("localhost") || domain.includes("192.");

export const isStaging = domain.includes("vercel.app") || isLocalhost || domain.includes("staging");

export const SUBDOMAINPREFIXES = {
  member: isStaging ? "app-" : "app.",
  mobile: isStaging ? "mobile-" : "mobile.",
};

export const MAIN_DOMAIN = domain
  .replace(SUBDOMAINPREFIXES["member"], "")
  .replace(SUBDOMAINPREFIXES["mobile"], "");

export const SUBDOMAINS = {
  member: isLocalhost
    ? `${APP_SCHEME}//${MAIN_DOMAIN}:${APP_PORT}/`
    : `${APP_SCHEME}//${SUBDOMAINPREFIXES.member}${MAIN_DOMAIN}:${APP_PORT}/`,
  mobile: isLocalhost
    ? `${APP_SCHEME}//${MAIN_DOMAIN}:${APP_PORT}/`
    : `${APP_SCHEME}//${SUBDOMAINPREFIXES.mobile}${MAIN_DOMAIN}:${APP_PORT}/`,
};

export const CooperativeTransactionWalletTypeMap: Record<
  CooperativeTransactionWalletType,
  { title: string; transactionCategory: TransactionCategory }
> = {
  cooperative_wallet: {
    title: "Cooperative fund movement",
    transactionCategory: "cooperative_wallet",
  },
  wallet: {
    title: "Cooperative fund movement",
    transactionCategory: "cooperative_wallet",
  },
  deposit: { title: "Deposit", transactionCategory: "deposit" },
  loandisbursement: {
    title: "Loan disbursement",
    transactionCategory: "loanDisbursement",
  },
  loanDisbursement: {
    title: "Loan disbursement",
    transactionCategory: "loanDisbursement",
  },
  loanrepayment: {
    title: "Loan repayment",
    transactionCategory: "loanrepayment",
  },
  registrationfee: {
    title: "Registration fee",
    transactionCategory: "registrationfee",
  },
  saving: { title: "Contribution", transactionCategory: "contribution" },
  shares: { title: "Share", transactionCategory: "shares" },
  withdrawal: {
    title: "Cooperative fund movement",
    transactionCategory: "withdrawal",
  },
};

export const loanStatusMap: Record<
  LoanStatus,
  {
    text: string;
    tagVariant: TagType;
  }
> = {
  active: {
    text: "Active",
    tagVariant: "info",
  },
  denied: {
    text: "Rejected",
    tagVariant: "danger",
  },
  disburse: {
    text: "Ongoing Disbursement",
    tagVariant: "pending",
  },
  failedDisbursement: {
    text: "Failed Disbursement",
    tagVariant: "danger",
  },
  "in-progress": {
    text: "Pending Approval",
    tagVariant: "pending",
  },
  paidinfull: {
    text: "Completed",
    tagVariant: "success",
  },
  waiting: {
    text: "Pending Guarantor",
    tagVariant: "pending",
  },
  waitingDisbursement: {
    text: "Awaiting Disbursement",
    tagVariant: "pending",
  },
  cancelled: {
    text: "Cancelled",
    tagVariant: "danger",
  },
};

export const loanRepaymentTypeMap: Record<
  LoanRepaymentType,
  {
    text: string;
  }
> = {
  full: { text: "Full Repayment" },
  monthly: { text: "Monthly Repayment" },
  custom: { text: "Partial Repayment" },
};

export const loanDisbursementMethodMap: Record<
  LoanDisbursementMethod,
  {
    text: string;
  }
> = {
  bank: {
    text: "My bank account",
  },
  wallet: {
    text: "My thriftcorp wallet",
  },
};

export const loanrepaymentMethodMap: Record<
  LoanRepaymentMethod,
  {
    text: string;
    shortText: string;
    configText: string;
    configSubtext: string;
  }
> = {
  wallet: {
    text: "Repay from my thriftcorp wallet",
    shortText: "ThriftCorp wallet",
    configText: "Wallet",
    configSubtext: "Wallet",
  },
  bank: {
    text: "Repay from my bank account",
    shortText: "Bank",
    configText: "Direct Debit on Bank Accounts",
    configSubtext:
      "Members can repay their loans through automatic debit on their specified bank accounts.",
  },
  card: {
    text: "Repay with my card",
    shortText: "Card",
    configText: "Direct Debit on Bank Cards",
    configSubtext:
      "Members can repay their loans through automatic debit on their specified bank cards.",
  },
  manual: {
    text: "Make payment manually",
    shortText: "Manual repayment",
    configText: "Manual Repayment",
    configSubtext:
      "Members can repay their loans through offline transactions, which are then logged and recorded in-app.",
  },
};

export const COUNTRIES: Record<
  string,
  {
    name: string;
    flag: string;
    countryCode: string;
    nationality: string;
  }
> = Object.fromEntries(
  allCountries.map((c) => {
    const englishDemonym = c.demonyms?.eng?.m ?? null; // or choose "f"
    const countryCode =
      c.idd?.root && c.idd?.suffixes?.length
        ? `${c.idd.root}${c.idd.suffixes[0]}`
        : "";

    return [
      c.cca2 as CountryCode,
      {
        name: c.name.common,
        flag: c.flag,
        countryCode,
        nationality: englishDemonym,
      },
    ];
  }),
);

export const PRIORITY_COUNTRY_CODES: CountryCode[] = ["NG"];
